 
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getProjectFinance, updateProjectFinance, addProjectCost, deleteProjectCost, getEmployees } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { formatDate, formatEur, COST_TYPES } from '../lib/workStatus';
import { Coins, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';

const costLabel = (type) => COST_TYPES.find((c) => c.value === type)?.label || type;

const Row = ({ label, value, negative, bold, testId }) => (
  <div className={`flex justify-between py-2 border-b border-gray-100 last:border-0 text-sm ${bold ? 'font-bold text-base' : ''}`} data-testid={testId}>
    <span className={bold ? 'text-[#1F3D2B]' : 'text-[#2E2E2E]/70'}>{label}</span>
    <span className={negative ? 'text-red-700' : bold ? 'text-[#1F3D2B]' : ''}>{negative ? '− ' : ''}{value}</span>
  </div>
);

export const ProjectFinancePanel = ({ projectId }) => {
  const [finance, setFinance] = useState(null);
  const [settings, setSettings] = useState({ client_amount: '', km_rate: '' });
  const [cost, setCost] = useState({ cost_type: 'fuel', date: new Date().toISOString().split('T')[0], amount: '', quantity: '', employee_id: '', note: '' });
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const isPaint = cost.cost_type === 'paint';

  useEffect(() => { getEmployees().then((d) => setEmployees(d || [])).catch(() => {}); }, []);

  const load = useCallback(async () => {
    try {
      const data = await getProjectFinance(projectId);
      setFinance(data);
      setSettings({ client_amount: data.client_amount || '', km_rate: data.km_rate || '' });
    } catch (error) {
      toast.error('Neizdevās ielādēt finanšu datus');
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      setFinance(await updateProjectFinance(projectId, { client_amount: Number(settings.client_amount || 0), km_rate: Number(settings.km_rate || 0) }));
      toast.success('Saglabāts');
    } catch (error) {
      toast.error('Neizdevās saglabāt');
    } finally {
      setSaving(false);
    }
  };

  const submitCost = async (e) => {
    e.preventDefault();
    if (isPaint && (!cost.quantity || Number(cost.quantity) <= 0)) { toast.error('Ievadiet balonu skaitu'); return; }
    if (!isPaint && (!cost.amount || Number(cost.amount) <= 0)) { toast.error('Ievadiet summu'); return; }
    try {
      setFinance(await addProjectCost(projectId, {
        cost_type: cost.cost_type,
        date: `${cost.date}T00:00:00`,
        amount: cost.amount === '' ? null : Number(cost.amount),
        quantity: isPaint ? Number(cost.quantity) : null,
        employee_id: cost.employee_id || null,
        note: cost.note || null
      }));
      setCost((p) => ({ ...p, amount: '', quantity: '', note: '' }));
      toast.success('Izmaksa pievienota');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās pievienot');
    }
  };

  const removeCost = async (costId) => {
    try { setFinance(await deleteProjectCost(projectId, costId)); } catch (error) { toast.error('Neizdevās dzēst'); }
  };

  if (!finance) return null;
  const profitPositive = finance.profit >= 0;

  return (
    <Card className="border-0 shadow-sm" data-testid="project-finance-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2"><Coins className="w-5 h-5 text-[#1F3D2B]" /> Objekta peļņa</CardTitle>
          <span className={`text-xl font-bold ${profitPositive ? 'text-green-700' : 'text-red-700'}`} data-testid="project-finance-profit">{formatEur(finance.profit)} <span className="text-sm font-medium">({finance.margin_percent}%)</span></span>
        </div>
        <p className="text-xs text-[#2E2E2E]/60">Tikai administratoram. Darbinieku izmaksas veidojas automātiski no apstiprinātajiem darba ierakstiem × likme.</p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end rounded-xl bg-[#f8faf8] p-3" data-testid="project-finance-settings">
          <div className="space-y-1">
            <Label className="text-xs">Klientam izrakstāmā summa (€)</Label>
            <Input type="number" step="0.01" value={settings.client_amount} onChange={(e) => setSettings((p) => ({ ...p, client_amount: e.target.value }))} data-testid="project-finance-client-amount" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Transporta likme (€/km){finance.km_rate_is_default ? ` · noklusējums ${formatEur(finance.default_km_rate)}` : ''}</Label>
            <Input type="number" step="0.01" value={settings.km_rate} onChange={(e) => setSettings((p) => ({ ...p, km_rate: e.target.value }))} placeholder={String(finance.default_km_rate ?? 0.3)} data-testid="project-finance-km-rate" />
          </div>
          <Button onClick={saveSettings} disabled={saving} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="project-finance-save"><Save className="w-4 h-4 mr-2" /> Saglabāt</Button>
        </div>

        <div data-testid="project-finance-breakdown">
          <Row label="Klientam izrakstāmā summa" value={formatEur(finance.client_amount)} testId="project-finance-row-client" />
          <Row label={`Darbinieku izmaksas (bruto ${formatEur(finance.employee_cost_gross)} + darba devēja VSAOI ${formatEur(finance.employer_vsaoi_cost)})${finance.pending_employee_cost ? ` · vēl neapstiprināti ${formatEur(finance.pending_employee_cost)}` : ''}`} value={formatEur(finance.employee_cost)} negative testId="project-finance-row-employees" />
          <Row label={`Transporta izmaksas (${finance.total_km} km × ${formatEur(finance.km_rate)} = ${formatEur(finance.mileage_cost)} + manuālās)`} value={formatEur(finance.transport_cost)} negative testId="project-finance-row-transport" />
          <Row label="Degviela" value={formatEur(finance.fuel_cost)} negative testId="project-finance-row-fuel" />
          <Row label={`Krāsa (${finance.paint_cans || 0} baloni)`} value={formatEur(finance.paint_cost)} negative testId="project-finance-row-paint" />
          <Row label="Citas izmaksas" value={formatEur(finance.other_cost)} negative testId="project-finance-row-other" />
          <Row label="Objekta peļņa" value={formatEur(finance.profit)} bold testId="project-finance-row-profit" />
        </div>

        {finance.employee_breakdown.length > 0 && (
          <div className="text-xs text-[#2E2E2E]/60" data-testid="project-finance-employees">
            Pa darbiniekiem: {finance.employee_breakdown.map((e) => `${e.employee_name} ${formatEur(e.cost)}`).join(' · ')}
          </div>
        )}

        <form onSubmit={submitCost} className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end" data-testid="project-cost-form">
          <div className="space-y-1">
            <Label className="text-xs">Izmaksu tips</Label>
            <Select value={cost.cost_type} onValueChange={(v) => setCost((p) => ({ ...p, cost_type: v }))}>
              <SelectTrigger data-testid="project-cost-type"><SelectValue /></SelectTrigger>
              <SelectContent>{COST_TYPES.map((c) => <SelectItem key={c.value} value={c.value} data-testid={`project-cost-type-${c.value}`}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Datums</Label><Input type="date" value={cost.date} onChange={(e) => setCost((p) => ({ ...p, date: e.target.value }))} data-testid="project-cost-date" /></div>
          {isPaint && <div className="space-y-1"><Label className="text-xs">Baloni (gab.)</Label><Input type="number" step="1" min="0" value={cost.quantity} onChange={(e) => setCost((p) => ({ ...p, quantity: e.target.value }))} data-testid="project-cost-quantity" /></div>}
          <div className="space-y-1"><Label className="text-xs">{isPaint ? 'Summa (€, auto pēc cenas)' : 'Summa (€)'}</Label><Input type="number" step="0.01" value={cost.amount} onChange={(e) => setCost((p) => ({ ...p, amount: e.target.value }))} placeholder={isPaint ? 'auto' : ''} data-testid="project-cost-amount" /></div>
          <div className="space-y-1">
            <Label className="text-xs">Darbinieks (nav obligāti)</Label>
            <Select value={cost.employee_id || 'none'} onValueChange={(v) => setCost((p) => ({ ...p, employee_id: v === 'none' ? '' : v }))}>
              <SelectTrigger data-testid="project-cost-employee"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`project-cost-employee-${e.id}`}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Piezīme</Label><Input value={cost.note} onChange={(e) => setCost((p) => ({ ...p, note: e.target.value }))} data-testid="project-cost-note" /></div>
          <Button type="submit" variant="outline" data-testid="project-cost-submit">Pievienot izmaksu</Button>
        </form>

        {finance.costs.length > 0 && (
          <div className="divide-y text-sm" data-testid="project-cost-list">
            {finance.costs.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-2" data-testid={`project-cost-row-${c.id}`}>
                <span>{formatDate(c.date)} · <strong>{costLabel(c.cost_type)}</strong>{c.cost_type === 'paint' && c.quantity ? ` · ${c.quantity} gab. × ${formatEur(c.unit_price)}` : ''}{c.employee_name ? ` · ${c.employee_name}` : ''}{c.note ? ` · ${c.note}` : ''}</span>
                <span className="flex items-center gap-2">{formatEur(c.amount)}<Button size="sm" variant="ghost" onClick={() => removeCost(c.id)} data-testid={`project-cost-delete-${c.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button></span>
              </div>
            ))}
          </div>
        )}
        {finance.mileage.length > 0 && (
          <p className="text-xs text-[#2E2E2E]/60">Kilometri objektam: {finance.mileage.map((m) => `${formatDate(m.date)} ${m.employee_name} ${m.kilometers} km`).join(' · ')}. <Link to="/portal/admin/mileage" className="underline">Pārvaldīt</Link></p>
        )}
      </CardContent>
    </Card>
  );
};

export default ProjectFinancePanel;
